import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";

import { Container, Grid } from "@material-ui/core/";
import { Close } from "@material-ui/icons";
import { Button } from "../../../../../components";
import LoadIcon from "../../../../../components/LoadIcon";
import StatusFlow from "../../../../../components/StatusFlow";
import {
  BreakpointValues,
  Colors,
  MODULES,
  OrderStatusEnum,
  SUB_MODULES,
  USER_ROLES,
} from "../../../../../constants";
import { mediaHelper, useWindowSize } from "../../../../../utils";
import MaterialSlider from "../materialSlider";
import { ERPLoading } from "../styles";

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.BLUE_LIGHT};
  width: 100%;
  height: 92px;

  ${mediaHelper.lg`
    font-size: 34px;
  `}
`;

const DrawerTitle = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 24px;
  font-weight: 700;
  color: ${Colors.SECONDARY_MEDIUM};

  ${mediaHelper.lg`
    display: block;
  `}
`;

const DrawerActionWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  & > button {
    margin-left: 24px;
  }
`;

const DrawerContent = styled.div`
  display: flex;
  background: ${Colors.BLUE_DARKER};
  background: -moz-linear-gradient(
    left,
    ${Colors.BLUE_DARKER} 0%,
    ${Colors.BLUE_DARKER} 50%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 1) 100%
  );
  background: -webkit-gradient(
    left top,
    right top,
    color-stop(0%, ${Colors.BLUE_DARKER}),
    color-stop(50%, ${Colors.BLUE_DARKER}),
    color-stop(50%, rgba(255, 255, 255, 1)),
    color-stop(100%, rgba(255, 255, 255, 1))
  );
  background: -webkit-linear-gradient(
    left,
    ${Colors.BLUE_DARKER} 0%,
    ${Colors.BLUE_DARKER} 50%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 1) 100%
  );
  background: -o-linear-gradient(
    left,
    ${Colors.BLUE_DARKER} 0%,
    ${Colors.BLUE_DARKER} 50%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 1) 100%
  );
  background: -ms-linear-gradient(
    left,
    ${Colors.BLUE_DARKER} 0%,
    ${Colors.BLUE_DARKER} 50%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 1) 100%
  );
  background: linear-gradient(
    to right,
    ${Colors.BLUE_DARKER} 0%,
    ${Colors.BLUE_DARKER} 50%,
    ${Colors.GRAY_LIGHEST_LIGHT} 50%,
    ${Colors.GRAY_LIGHEST_LIGHT} 100%
  );
  filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#c6deee', endColorstr='#ffffff', GradientType=1 );
`;

const StyledContainer = styled(Container)`
  padding: 24px;
`;

const StyledGrid = styled(Grid)`
  margin-top: 0 !important;
`;

const StyledGridLeft = styled(Grid)`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.BLUE_DARKER};
`;

const StyledGridRight = styled(Grid)`
  display: flex;
  flex-direction: column;
  background: ${Colors.GRAY_LIGHEST_LIGHT};
`;

const BoxContent = styled.div`
  display: flex;
  align-items: center;
  padding: 24px;

  flex-direction: column;

  ${mediaHelper.lg`
    padding: 24px 24px 24px 16px;
  `}
`;

const OrderDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const LabelText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${Colors.GRAY_DARKEST};
  margin-bottom: 8px;
`;

const ValueText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${Colors.GRAY_LIGHT};
  padding: 24px;
  border-radius: 8px;
  gap: 16px;
`;

const ActionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 16px;

  & button {
    margin-bottom: 16px;
  }

  ${mediaHelper.lg`
    flex-direction: row;

    & button {
      margin-bottom: 0;
    }
  `}
`;

const ActionRight = styled.div`
  ${mediaHelper.lg`
    margin-left: 16px;
  `}
`;

const RowTitleText = styled.strong`
  display: flex;
  color: ${Colors.SECONDARY_MEDIUM};
  font-size: 20px;
  font-weight: 700;
`;

const OrderTitleText = styled.strong`
  display: flex;
  color: ${Colors.SECONDARY_MEDIUM};
  font-size: 14px;
  font-weight: 400;
`;

const StyledButton = styled(Button)`
  width: 100%;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.4px;
`;

const StyledIcon = styled(Close)`
  margin-left: 8px;
`;

const StyledStatusFlow = styled(StatusFlow)`
  padding-left: 8px;
`;

const PromoCicleName = styled.span`
  font-size: 17px;
  color: ${Colors.SECONDARY_MEDIUM};
`;

const OrderStatusHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const OrderDetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const OrderDetailsContentItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const OrderDetailsDelivery = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  margin-bottom: 16px;
`;

const OrderDetail = ({ ...props }) => {
  const {
    userData,
    isLoadingTable,
    orderNumber,
    promotionalCicle,
    orderData,
    orderDetailData,
    onRequestTryAgain,
    onCloseDrawer,
    onExportSelectedData,
    openApproveOrderDialog,
    openRefuseOrderDialog,
    openCancelOrderDialog,
    handleOpenUrls,
  } = props;

  const currentWindowSize = useWindowSize();

  const { data } = orderDetailData;

  const renderExpectedDate = () => {
    const expectedDateFormatted =
      data?.statusList.length > 0 &&
      data?.statusList.find((value) => value.title === "Entrega")?.expectedDate
        ? new Date(
            data.statusList.find(
              (value) => value.title === "Entrega"
            ).expectedDate
          ).toLocaleDateString("pt-BR")
        : "--";

    return (
      <InfoContent>
        <LabelText>Previsão de entrega</LabelText>
        <ValueText>{expectedDateFormatted}</ValueText>
      </InfoContent>
    );
  };

  const renderReceiverAddress = (addressData) => {
    if (!addressData) return null;

    const {
      street,
      addressNumber = "SN",
      neighborhood,
      city,
      cep,
    } = addressData;

    return (
      <InfoContent>
        <LabelText>Endereço de entrega</LabelText>
        <ValueText>
          {`${street}, ${addressNumber} - ${neighborhood} - ${city} - CEP ${cep}`}
        </ValueText>
      </InfoContent>
    );
  };

  const rolesPermitted = userData.userRole === USER_ROLES.COMPANY;
  // console.log(orderData, "orderData");

  return (
    <>
      {/* <Container maxWidth="lg">
        <Grid container justify="space-between" alignItems="center" spacing={3}>
          <Grid item lg={7} md={7}>
            <DrawerTitle>
              Pedido{" "}
              {orderData.length > 0
                ? orderData.map(({ data }) => (
                    <>
                      {orderNumber === null ? data.id : orderNumber}
                      <br />
                      <PromoCicleName>
                        {data.promotionalCicle === ""
                          ? "Avulso"
                          : data.promotionalCicle}
                      </PromoCicleName>
                    </>
                  ))
                : null}
            </DrawerTitle>
          </Grid>
          <Grid item lg={5} md={5}>
            <DrawerActionWrapper>
              <Button
                size="large"
                color="secondary"
                handleClick={onCloseDrawer}
              >
                {currentWindowSize.width >= BreakpointValues.md ? "Fechar" : ""}
                <StyledIcon fontSize="small" />
              </Button>
            </DrawerActionWrapper>
          </Grid>
        </Grid>
      </Container> */}
      <StyledContainer>
        {isLoadingTable ? (
          <StyledGridRight item lg={9} md={9} xs={12}>
            <ERPLoading>
              <LoadIcon color="#394E98" />
              <span>
                Aguarde enquanto estamos
                <br />
                gerando os arquivos ERP...
              </span>
            </ERPLoading>
          </StyledGridRight>
        ) : (
          <BoxContent>
            <OrderStatusHeader>
              <RowTitleText>Status do pedido</RowTitleText>
              <OrderTitleText>Pedido {orderNumber}</OrderTitleText>
              <OrderTitleText>
                Ciclo promocional: {promotionalCicle}
              </OrderTitleText>
            </OrderStatusHeader>
            {/* <SectionHeader>
                {data && data.statusList && (
                      <ActionHeader>
                        {rolesPermitted && (
                          <>
                            {data.canRefuse && (
                              <Button
                                size="small"
                                color="secondary"
                                variant="outlined"
                                handleClick={() => {
                                  openRefuseOrderDialog(
                                    orderData[0].data.id,
                                    true
                                  );
                                }}
                              >
                                Recusar
                              </Button>
                            )}
                            {data.canApprove && (
                              <ActionRight>
                                <Button
                                  size="small"
                                  color="primary"
                                  variant="contained"
                                  handleClick={() => {
                                    openApproveOrderDialog(
                                      orderData[0].data.id,
                                      true
                                    );
                                  }}
                                >
                                  Aprovar
                                </Button>
                              </ActionRight>
                            )}
                          </>
                        )}
                        {(((orderData[0].data.status ===
                          OrderStatusEnum.ORDER_PENDING_APPROVAL ||
                          orderData[0].data.status ===
                            OrderStatusEnum.BILLING_ORDER) &&
                          userData.userRole === USER_ROLES.COMPANY) ||
                          (orderData[0].data.status ===
                            OrderStatusEnum.ORDER_PENDING_APPROVAL &&
                            userData.userRole === USER_ROLES.AGENT)) && (
                          <ActionRight>
                            <Button
                              size="small"
                              color="secondary"
                              variant="contained"
                              handleClick={() => {
                                openCancelOrderDialog(
                                  orderData[0].data.id,
                                  true
                                );
                              }}
                            >
                              Cancelar pedido
                            </Button>
                          </ActionRight>
                        )}
                      </ActionHeader>
                    )}
              </SectionHeader> */}
            <StyledStatusFlow
              statusList={data ? data.statusList : []}
              onRequestTryAgain={onRequestTryAgain}
            />
            <SectionHeader>
              <RowTitleText>Informações do pedido</RowTitleText>
              <OrderDetails>
                {data.receiver ? (
                  <OrderDetailsContent>
                    <OrderDetailsDelivery>
                      {renderReceiverAddress(data.receiver.address)}
                    </OrderDetailsDelivery>
                    <OrderDetailsContentItem>
                      <InfoContent>
                        <LabelText>Destinatário</LabelText>
                        <ValueText>
                          {data.receiver.name
                            ? data.receiver.name
                            : "Não definido"}
                        </ValueText>
                      </InfoContent>
                      {data.createdBy && (
                        <InfoContent>
                          <LabelText>Criado por</LabelText>
                          <ValueText>{data.createdBy}</ValueText>
                        </InfoContent>
                      )}
                      {data.approvedBy && (
                        <InfoContent>
                          <LabelText>Aprovado por</LabelText>
                          <ValueText>{data.approvedBy}</ValueText>
                        </InfoContent>
                      )}
                      {data.refusedBy && (
                        <InfoContent>
                          <LabelText>Recusado por</LabelText>
                          <ValueText>{data.refusedBy}</ValueText>
                        </InfoContent>
                      )}
                      {data.canceledBy && (
                        <InfoContent>
                          <LabelText>Cancelado por</LabelText>
                          <ValueText>{data.canceledBy}</ValueText>
                        </InfoContent>
                      )}
                      {renderExpectedDate()}
                      {data.canceledBy && (
                        <InfoContent>
                          <LabelText>Quantidade de pré-pedidos</LabelText>
                          <ValueText>{data.canceledBy}</ValueText>
                        </InfoContent>
                      )}
                    </OrderDetailsContentItem>
                  </OrderDetailsContent>
                ) : (
                  <InfoContent>
                    <LabelText>Destinatário</LabelText>
                    <ValueText>Nenhum destinatário para exibir</ValueText>
                  </InfoContent>
                )}
              </OrderDetails>
            </SectionHeader>
          </BoxContent>
        )}
      </StyledContainer>
    </>
  );
};

OrderDetail.propTypes = {
  orderNumber: PropTypes.string,
  orderData: PropTypes.array,
  orderDetailData: PropTypes.object,
  onCloseDrawer: PropTypes.func,
  onExportSelectedData: PropTypes.func,
  openApproveOrderDialog: PropTypes.func,
  openRefuseOrderDialog: PropTypes.func,
  openCancelOrderDialog: PropTypes.func,
  handleOpenUrls: PropTypes.func,
};

OrderDetail.defaultProps = {
  orderNumber: "",
  orderData: [],
  orderDetailData: {},
  onCloseDrawer: () => {},
  onExportSelectedData: () => {},
  openApproveOrderDialog: () => {},
  openRefuseOrderDialog: () => {},
  openCancelOrderDialog: () => {},
  handleOpenUrls: () => {},
};

export default OrderDetail;
